import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useVersionPoll } from '@/hooks/useVersionPoll';

// VersionAutoReload upgrades a long-lived tab to a freshly deployed SPA build
// WITHOUT a visible "new version — tap to refresh" prompt. The goal is an
// invisible upgrade: the user shouldn't have to notice, decide, or click.
//
// Strategy: when a new build is detected, arm. On the user's NEXT route
// navigation, hard-reload. Riding a navigation the user initiated means we
// never reload out from under them mid-screen (no lost form input — they've
// left that screen) and the reload reads as an ordinary, slightly-slower page
// load. Combined with the `vite:preloadError` handler in main.tsx (which
// recovers transparently if an old tab requests a chunk a deploy removed) and
// the edge-Caddy upstream retries (which hide API container swaps), a deploy
// is effectively invisible to anyone with the app open.
//
// Mounted inside <BrowserRouter> (see main.tsx) so useLocation() is available.
export function VersionAutoReload() {
  const { updateAvailable } = useVersionPoll();
  const location = useLocation();
  // Path we were on when the update first landed. Null until armed.
  const armedAtPath = useRef<string | null>(null);

  useEffect(() => {
    if (updateAvailable && armedAtPath.current === null) {
      armedAtPath.current = location.pathname;
    }
  }, [updateAvailable, location.pathname]);

  useEffect(() => {
    if (armedAtPath.current !== null && location.pathname !== armedAtPath.current) {
      window.location.reload();
    }
  }, [location.pathname]);

  return null;
}
